'use client';

import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';

// Formspree Form ID - Replace with actual ID after creating form
const FORMSPREE_FORM_ID = process.env.NEXT_PUBLIC_FORMSPREE_FORM_ID || 'xyzabc'; // Placeholder

// Form validation schema
const cateringFormSchema = z.object({
  full_name: z.string().min(1, 'Full name is required'),
  company_organization: z.string().optional(),
  email: z.string().email('Valid email is required'),
  phone_number: z.string().min(10, 'Valid phone number is required'),
  event_type: z.string().min(1, 'Event type is required'),
  event_date: z.string().min(1, 'Event date is required'),
  service_window: z.string().min(1, 'Service window is required'),
  event_address_notes: z.string().min(1, 'Event address is required'),
  guest_count: z.string().min(1, 'Guest count is required'),
  package_selection: z.string().min(1, 'Package selection is required'),
  protein_preference: z.array(z.string()).optional(),
  dietary_needs: z.array(z.string()).optional(),
  guest_payment_preference: z.string().optional(),
  power_water_available: z.string().optional(),
  special_requests: z.string().optional(),
  referral_source: z.string().optional(),
});

type CateringFormData = z.infer<typeof cateringFormSchema>;

interface CateringFormProps {
  onSuccess?: () => void;
}

export function CateringForm({ onSuccess }: CateringFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<CateringFormData>({
    resolver: zodResolver(cateringFormSchema),
    defaultValues: {
      protein_preference: [],
      dietary_needs: [],
    },
  });

  const onSubmit = async (data: CateringFormData) => {
    setIsSubmitting(true);
    try {
      // Submit to Formspree
      const formspreeUrl = `https://formspree.io/f/${FORMSPREE_FORM_ID}`;

      // Convert array fields to comma-separated strings for Formspree
      const formData = new FormData();

      // Add all form fields
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Convert arrays to comma-separated strings
          formData.append(key, value.join(', '));
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, String(value));
        }
      });

      const response = await fetch(formspreeUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit catering request');
      }

      setShowSuccess(true);
      reset();
      toast.success('Catering request submitted!');

      if (onSuccess) onSuccess();

      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
        <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">
          Thank You!
        </h3>
        <p className="text-base sm:text-lg text-gray-600 max-w-md">
          Your catering request has been received. The Mo's Burger team will follow up soon at the email or phone number provided.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Contact Information Section */}
        <div className="bg-white rounded-2xl border border-border p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-6">
            Contact Information
          </h3>

          <div className="space-y-5">
            {/* Full Name */}
            <div>
              <Label htmlFor="full_name" className="text-sm font-bold text-gray-700 mb-2 block">
                Full Name *
              </Label>
              <Input
                id="full_name"
                placeholder="Your full name"
                {...register('full_name')}
                className="border border-border rounded-lg"
              />
              {errors.full_name && (
                <p className="text-red-500 text-sm mt-1">{errors.full_name.message}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <Label htmlFor="company_organization" className="text-sm font-bold text-gray-700 mb-2 block">
                Company / Organization
              </Label>
              <Input
                id="company_organization"
                placeholder="Optional"
                {...register('company_organization')}
                className="border border-border rounded-lg"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-sm font-bold text-gray-700 mb-2 block">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                {...register('email')}
                className="border border-border rounded-lg"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone_number" className="text-sm font-bold text-gray-700 mb-2 block">
                Phone Number *
              </Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="(555) 123-4567"
                {...register('phone_number')}
                className="border border-border rounded-lg"
              />
              {errors.phone_number && (
                <p className="text-red-500 text-sm mt-1">{errors.phone_number.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Event Details Section */}
        <div className="bg-white rounded-2xl border border-border p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-6">
            Event Details
          </h3>

          <div className="space-y-5">
            {/* Event Type */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Event Type *
              </Label>
              <Controller
                name="event_type"
                control={control}
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    <div className="space-y-2">
                      {['Corporate lunch', 'Private party', 'Wedding', 'School', 'Festival', 'Other'].map((type) => (
                        <div key={type} className="flex items-center space-x-2">
                          <RadioGroupItem value={type} id={`event-${type}`} />
                          <Label htmlFor={`event-${type}`} className="font-medium cursor-pointer">
                            {type}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              />
              {errors.event_type && (
                <p className="text-red-500 text-sm mt-1">{errors.event_type.message}</p>
              )}
            </div>

            {/* Event Date */}
            <div>
              <Label htmlFor="event_date" className="text-sm font-bold text-gray-700 mb-2 block">
                Event Date *
              </Label>
              <Input
                id="event_date"
                type="date"
                {...register('event_date')}
                className="border border-border rounded-lg"
              />
              {errors.event_date && (
                <p className="text-red-500 text-sm mt-1">{errors.event_date.message}</p>
              )}
            </div>

            {/* Service Window */}
            <div>
              <Label htmlFor="service_window" className="text-sm font-bold text-gray-700 mb-2 block">
                Service Window *
              </Label>
              <Input
                id="service_window"
                placeholder="Start–end time (e.g., 12:00 PM - 2:00 PM)"
                {...register('service_window')}
                className="border border-border rounded-lg"
              />
              {errors.service_window && (
                <p className="text-red-500 text-sm mt-1">{errors.service_window.message}</p>
              )}
            </div>

            {/* Event Address */}
            <div>
              <Label htmlFor="event_address_notes" className="text-sm font-bold text-gray-700 mb-2 block">
                Event Address + Parking/Loading Notes *
              </Label>
              <Textarea
                id="event_address_notes"
                placeholder="Full address and any parking/loading information"
                {...register('event_address_notes')}
                className="border-2 border-gray-300 rounded-lg min-h-24"
              />
              {errors.event_address_notes && (
                <p className="text-red-500 text-sm mt-1">{errors.event_address_notes.message}</p>
              )}
            </div>

            {/* Guest Count */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Estimated Guest Count *
              </Label>
              <Controller
                name="guest_count"
                control={control}
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    <div className="space-y-2">
                      {['25–49', '50–74', '75–99', '100–149', '150+'].map((count) => (
                        <div key={count} className="flex items-center space-x-2">
                          <RadioGroupItem value={count} id={`guests-${count}`} />
                          <Label htmlFor={`guests-${count}`} className="font-medium cursor-pointer">
                            {count}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              />
              {errors.guest_count && (
                <p className="text-red-500 text-sm mt-1">{errors.guest_count.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Package & Preferences Section */}
        <div className="bg-white rounded-2xl border border-border p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-6">
            Package & Preferences
          </h3>

          <div className="space-y-5">
            {/* Package Selection */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Package Selection *
              </Label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-sm text-blue-800">
                <p className="font-medium mb-2">Pricing includes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>$175 transportation fee</li>
                  <li>$2 per mile after a 10-mile radius</li>
                </ul>
              </div>
              <Controller
                name="package_selection"
                control={control}
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    <div className="space-y-2">
                      {['Basic - $16 per person', 'Deluxe - $21 per person', 'Premium - $27 per person', 'Not sure'].map((pkg) => (
                        <div key={pkg} className="flex items-center space-x-2">
                          <RadioGroupItem value={pkg} id={`package-${pkg}`} />
                          <Label htmlFor={`package-${pkg}`} className="font-medium cursor-pointer">
                            {pkg}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              />
              {errors.package_selection && (
                <p className="text-red-500 text-sm mt-1">{errors.package_selection.message}</p>
              )}
            </div>

            {/* Protein Preference */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Protein Preference Split
              </Label>
              <Controller
                name="protein_preference"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    {['Burger', 'Falafel', 'Steak & cheese'].map((protein) => (
                      <div key={protein} className="flex items-center gap-3">
                        <Checkbox
                          id={`protein-${protein}`}
                          checked={field.value?.includes(protein) || false}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), protein]
                              : field.value?.filter(p => p !== protein) || [];
                            field.onChange(newValue);
                          }}
                        />
                        <Label htmlFor={`protein-${protein}`} className="font-medium cursor-pointer">
                          {protein}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>

            {/* Dietary Needs */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Dietary Needs
              </Label>
              <Controller
                name="dietary_needs"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    {['Halal', 'Vegetarian', 'No pork', 'Gluten sensitive', 'Other'].map((dietary) => (
                      <div key={dietary} className="flex items-center gap-3">
                        <Checkbox
                          id={`dietary-${dietary}`}
                          checked={field.value?.includes(dietary) || false}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...(field.value || []), dietary]
                              : field.value?.filter(d => d !== dietary) || [];
                            field.onChange(newValue);
                          }}
                        />
                        <Label htmlFor={`dietary-${dietary}`} className="font-medium cursor-pointer">
                          {dietary}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="bg-white rounded-2xl border border-border p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-6">
            Additional Information
          </h3>

          <div className="space-y-5">
            {/* Payment Structure */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Would You Like Guests to Pay Individually?
              </Label>
              <Controller
                name="guest_payment_preference"
                control={control}
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    <div className="space-y-2">
                      {['Host pays', 'Guests pay', 'Not sure'].map((structure) => (
                        <div key={structure} className="flex items-center space-x-2">
                          <RadioGroupItem value={structure} id={`payment-${structure}`} />
                          <Label htmlFor={`payment-${structure}`} className="font-medium cursor-pointer">
                            {structure}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            {/* Power/Water */}
            <div>
              <Label className="text-sm font-bold text-gray-700 mb-3 block">
                Power/Water Available On-Site?
              </Label>
              <Controller
                name="power_water_available"
                control={control}
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange}>
                    <div className="space-y-2">
                      {['Yes', 'No', 'Not sure'].map((available) => (
                        <div key={available} className="flex items-center space-x-2">
                          <RadioGroupItem value={available} id={`power-${available}`} />
                          <Label htmlFor={`power-${available}`} className="font-medium cursor-pointer">
                            {available}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            {/* Special Requests */}
            <div>
              <Label htmlFor="special_requests" className="text-sm font-bold text-gray-700 mb-2 block">
                Any Special Requests?
              </Label>
              <Textarea
                id="special_requests"
                placeholder="Tell us any special requests or requirements..."
                {...register('special_requests')}
                className="border-2 border-gray-300 rounded-lg min-h-24"
              />
            </div>

            {/* How Did You Hear About Us */}
            <div>
              <Label htmlFor="referral_source" className="text-sm font-bold text-gray-700 mb-2 block">
                How Did You Hear About Us?
              </Label>
              <Input
                id="referral_source"
                placeholder="Instagram, Facebook, referral, etc."
                {...register('referral_source')}
                className="border border-border rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-black text-base sm:text-lg py-6 sm:py-8 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Catering Request'
          )}
        </Button>
      </form>
    </div>
  );
}
